import { DerivedAvailabilityStatusInternal } from '@commerce-product-webcomponents/sdk';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  Theme,
  useTranslation,
  useSession,
  useNextgenRouter,
  Modal,
  TextAlign,
  ButtonAlignment,
} from '@nextgen-web-framework/all';
import { LoginWrapper } from '@commerce-identity-webcomponents/ui';
import { useEffect, useState } from 'react';
import { ProductLink } from '../Hoc';
import {
  BasicDetailsComponent,
  DerivedAvailabilityStatusComponentGroupKey,
  DetailsType,
  IdAndSizeType,
  ImageComponent,
  ImageType,
  includesDerivedAvailabilityStockStatus,
  ProductIdAndSizeComponent,
  StockStatusComponent,
  StockStatusComponentType,
} from '../Molecules';
import APointRedeemInfoDrawer from '../Molecules/APointRedeemInfoDrawer/APointRedeemInfoDrawer';
import { type APointRedeemCardPropsTypes } from './types/propsTypes';
import styles from './APointRedeemCard.module.scss';
import { getLoginProps, getSignUpUrl } from './ApointRedeemCardUtil';

/** @deprecated This component is deprecated from UI. Use the same component from ui-composite. */
const APointRedeemCard = (props: APointRedeemCardPropsTypes): JSX.Element => {
  const {
    productData,
    showAPointAdditionalInfo,
    productQuantity,
    isFlexibleWidthCard = false,
    dateSubText,
    className = '',
  } = props;

  const {
    brandName,
    productName,
    mainLink,
    alt,
    productId,
    productSize,
    aPointPrice,
    aPointPriceCompanionText,
    derivedAvailabilityStatus,
    firstAvailableFutureDate,
  } = productData;

  const [showApointRedeemDrawer, setShowApointRedeemDrawer] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isAboUser, setIsAboUser] = useState(false);
  const router = useNextgenRouter();
  const t = useTranslation();
  const { isABO, isAnonymous, session } = useSession();
  const conditionalAPointPrice = aPointPrice ?? '';
  const aPointText = `${conditionalAPointPrice} ${t('product.aPointRedeemCard.aPointText')}`;
  const productLinkProps = {
    className: styles['width-inherit'],
    showLink: false,
    href: '',
    productId,
    productName,
  };

  useEffect(() => {
    if (isABO()) {
      setIsAboUser(true);
    }
  }, [session]);

  const onCloseModalHandler = () => {
    setShowModal(false);
  };

  const modalPrimaryButton = (
    <LoginWrapper {...getLoginProps(router)} className={styles['cpw-login-wrapper']}>
      <Button
        id={'primaryBtn'}
        title={t('product.aPointRedeemCard.modal.primaryButton')}
        buttonStyle={ButtonStyles.SOLID}
        size={ButtonSize.MEDIUM}
        data-testid="primary-btn-data-testid"
        buttonWidth={ButtonWidth.FULL_WIDTH}
      />
    </LoginWrapper>
  );

  const modalSecondaryButton = (
    <Button
      onClick={() => router.replace?.(getSignUpUrl())}
      id={'secondaryBtn'}
      title={t('product.aPointRedeemCard.modal.secondaryButton')}
      buttonStyle={ButtonStyles.OUTLINE}
      size={ButtonSize.MEDIUM}
      data-testid="secondary-btn-data-testid"
      buttonWidth={ButtonWidth.FULL_WIDTH}
    />
  );

  const modalComp = (
    <Modal
      headline={t('product.aPointRedeemCard.modal.heading')}
      primaryButtonLabel={t('product.aPointRedeemCard.modal.primaryButton')}
      primaryButton={modalPrimaryButton}
      secondaryButton={modalSecondaryButton}
      onClose={onCloseModalHandler}
      textAlign={TextAlign.CENTER}
      buttonAlignment={ButtonAlignment.VERTICAL}
    />
  );

  const image = (type: string) => (
    <ProductLink {...productLinkProps}>
      <div className={`${styles['product-image-container']} ${styles['width-inherit']}`}>
        <ImageComponent link={mainLink} alt={alt} imagetype={type} />
      </div>
    </ProductLink>
  );

  const basicDetails = (type: string, mainText = brandName, subText = productName) => (
    <ProductLink {...productLinkProps}>
      <div className={styles['cpw-basic-details']}>
        <BasicDetailsComponent brandName={mainText} productName={subText} detailsType={type} />
      </div>
    </ProductLink>
  );

  const handleRedeemBtnClick = () => {
    if (isAnonymous()) {
      setShowModal(true);
    } else {
      setShowApointRedeemDrawer(true);
    }
  };

  const handleApointRedeemInfoDrawerCloseBtnClick = () => {
    setShowApointRedeemDrawer(false);
  };

  const redeemBtn = !isAboUser ? (
    <div className={styles['apoint-product-redeem-btn']}>
      <Button
        id={'redeem-apoint'}
        title={t('product.aPointRedeemCard.redeemBtn')}
        buttonStyle={ButtonStyles.SOLID}
        theme={Theme.BLACK}
        size={ButtonSize.SMALL}
        tabIndex={0}
        disabled={includesDerivedAvailabilityStockStatus(
          derivedAvailabilityStatus as DerivedAvailabilityStatusInternal,
          DerivedAvailabilityStatusComponentGroupKey.CTA_DISABLED_STATUSES,
        )}
        data-testid="redeem-apoint-btn"
        onClick={handleRedeemBtnClick}
        buttonWidth={ButtonWidth.FULL_WIDTH}
      />
    </div>
  ) : (
    <></>
  );

  return (
    <>
      {showModal && modalComp}
      <div
        className={`${styles['cpw-apoint-redeem-card']}  ${
          isFlexibleWidthCard && styles['flexible-card-width']
        } ${className}`}
        data-testid="cpw-apoint-redeem-card-test-id"
      >
        <div className={`${styles['cpw-apoint-card-container']}`} data-testid="apoint-redeem-card-container-testID">
          <div className={styles['product-image']} data-testid="apoint-redeem-card-image-testID">
            {image(ImageType.APOINT_REDEEM_CARD)}
          </div>
          <div className={styles['product-description']}>
            <div>
              {basicDetails(DetailsType.APOINT_REDEEM_PRODUCT_CARD)}
              <ProductIdAndSizeComponent
                productId={productId}
                idSizeType={IdAndSizeType.ID_SIZE_APOINT_CARD}
                showProductId={showAPointAdditionalInfo?.showProductId || false}
                productSize={showAPointAdditionalInfo?.showProductSize ? productSize : ''}
              />
            </div>
            <div className={styles['product-points-info']}>
              {basicDetails(DetailsType.APOINT_REDEEM_CARD_PRICE_DETAILS, aPointText, aPointPriceCompanionText)}
            </div>
            {derivedAvailabilityStatus && (
              <StockStatusComponent
                derivedAvailabilityStatus={derivedAvailabilityStatus}
                dateSubText={dateSubText}
                firstAvailableFutureDate={firstAvailableFutureDate}
                showSubText={true}
                stockStatusType={StockStatusComponentType.STOCK_STATUS_SEARCH_CARD}
              />
            )}
            {redeemBtn}
          </div>
        </div>
      </div>
      {showApointRedeemDrawer && (
        <APointRedeemInfoDrawer
          productData={productData}
          productQuantity={productQuantity}
          onClose={handleApointRedeemInfoDrawerCloseBtnClick}
        />
      )}
    </>
  );
};

export default APointRedeemCard;
export type { APointRedeemCardPropsTypes };
